import { Button, makeStyles, Text, tokens } from '@fluentui/react-components';
import { DismissRegular } from '@fluentui/react-icons';
import type { ReactNode } from 'react';

const useStyles = makeStyles({
  card: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    // A mesma caixa de um Input médio do Fluent: 32px com a borda, para o
    // campo travado não mudar de altura nem desalinhar a linha.
    height: '32px',
    boxSizing: 'border-box',
    // Item de grid tem largura mínima do conteúdo: sem isto, um nome longo
    // empurra o cartão para cima do campo vizinho em vez de truncar.
    width: '100%',
    minWidth: 0,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalXXS,
    backgroundColor: tokens.colorBrandBackground2,
    border: `${tokens.strokeWidthThin} solid ${tokens.colorBrandStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
  },
  icon: {
    display: 'inline-flex',
    flexShrink: 0,
    fontSize: '16px',
    color: tokens.colorBrandForeground1,
  },
  text: { display: 'block', flex: 1, minWidth: 0 },
  label: { color: tokens.colorBrandForeground1 },
  description: { color: tokens.colorNeutralForeground3 },
});

export interface LockedSelectionProps {
  readonly label: string;
  readonly description?: string | undefined;
  readonly icon?: ReactNode;
  /** Texto acessível do botão de trocar (`Trocar estabelecimento`). */
  readonly clearLabel: string;
  /** Sem ela, a seleção não pode ser trocada (campo só leitura). */
  readonly onClear?: (() => void) | undefined;
}

/**
 * Item já escolhido num campo de busca: fica travado, à vista, até a pessoa
 * pedir para trocar. Evita que um texto digitado pela metade pareça escolhido.
 * Uma linha só, na altura de um input; o texto inteiro fica no `title`.
 */
export function LockedSelection({
  label,
  description,
  icon,
  clearLabel,
  onClear,
}: LockedSelectionProps) {
  const styles = useStyles();
  const full = description ? `${label} · ${description}` : label;
  return (
    <div className={styles.card} title={full}>
      {icon && <span className={styles.icon}>{icon}</span>}
      <Text truncate wrap={false} className={styles.text}>
        <span className={styles.label}>
          <strong>{label}</strong>
        </span>
        {description && (
          <span className={styles.description}> · {description}</span>
        )}
      </Text>
      {onClear && (
        <Button
          appearance="transparent"
          size="small"
          icon={<DismissRegular />}
          aria-label={clearLabel}
          onClick={onClear}
        />
      )}
    </div>
  );
}
